import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash/isEqual'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { BankTransfer, Billing, BillingForm, SettlementsNames } from 'types/med'
import type { ImageProps, SettlementNameProps } from 'types/common'
import { StackItem } from 'ui/layout/StackItem'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import { Controller, FieldValues, useForm, FormProvider } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  MenuItem,
  Select,
  Paper,
  FormControl,
  FormHelperText,
  TextField,
  InputAdornment,
} from '@mui/material'
import Stack from 'ui/layout/Stack'
import { EditableThumbnailList } from 'ui/utils/Thumbnails'
import { UploadQuestionTooltip } from 'ui/shorthands/QuestionTooltip'
import ConfirmModal from './ConfirmModal'
import Amount from '../ReservationBillingModal/Amount'
import Modal from 'ui/layout/Modal'
import { Caption, Text1 } from 'ui/shorthands/Texts'
import { ApiErrorValuesProps } from 'config/api'
import StripeLink from 'ui/partials/StripeLink'
import BankInfo from 'ui/partials/BankInfo'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import { ApiErrorWithMessage, OfflineError } from 'app/client'
import { ConfirmContent, SendingContent } from 'ui/partials/ConfirmContent'
import theme from 'ui/theme'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    letterSpacing: '0.5px',
  },
  descriptionItem: {
    display: 'block',
    paddingTop: theme.spacing(1),
  },
  error: {
    fontWeight: 'normal',
  },
  disabled: {
    background: theme.palette.background.default,
  },
}))

type ReservationBillingModalProps = {
  /** 決済方法 */
  settlement: SettlementNameProps

  /** 銀行決済情報(settlement === bank_transferの場合に必須) */
  bankTransfer?: BankTransfer

  /** Stripe接続済みかどうか */
  isConnectedStripe: boolean

  /** 請求情報 */
  billings: Billing[]

  /**
   * `キャンセル` ボタンがクリックされた時のイベントハンドラ
   *  送信確認モーダルの「キャンセル」のクリックでは呼ばれない
   *  */
  onCancel: () => void

  /** 請求確認モーダルで「送信」ボタンがクリックされた時のイベントハンドラ */
  onSubmit: (billing: BillingForm) => Promise<void | ApiErrorValuesProps>

  /** モーダルが開いているかどうかを指定 */
  open: boolean
}
const ReservationBillingModal: React.FC<ReservationBillingModalProps> = ({
  settlement,
  bankTransfer,
  isConnectedStripe,
  billings,
  onCancel,
  onSubmit,
  open,
}: ReservationBillingModalProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const billing = billings.length ? billings[0] : undefined

  const submitLabel: string = billing ? t('再送信') : t('送信')

  const [apiError, setApiError] = useState<string>()

  const schema = yup.object().shape({
    amount: yup
      .number()
      .integer()
      .typeError(t('validation.半角数字を入力してください'))
      .required(t('validation.半角数字を入力してください'))
      .test(
        'space',
        t('validation.半角数字を入力してください'),
        (_, testContext) => {
          const context = testContext as yup.TestContext & {
            // TODO: 現在（v0.32.11）のTestContextの型定義にoriginalValueが存在しないためバージョンアップにより不要になったら削除
            originalValue?: any
          }
          return !(
            typeof context?.originalValue === 'string' &&
            context.originalValue.trim().match(/\s/)
          )
        }
      )
      .test(
        'max',
        t('validation.9,999,999円以下の金額を入力してください'),
        function (amount) {
          if (typeof amount !== 'number') return false
          return amount < 10000000
        }
      )
      .test(
        'min',
        t('validation.50円以上の金額を入力してください'),
        function (amount) {
          if (typeof amount !== 'number') return false
          if (this.parent.settlement_method === 'no_charge') {
            return amount === 0
          }
          return amount >= 50
        }
      ),
    detail: yup
      .object()
      .when((settlement_method: SettlementNameProps) =>
        settlement_method === 'link'
          ? yup.string().max(10000).required()
          : yup.string().max(10000).nullable()
      ),
    attachment_images: yup.array(),
    settlement_method: yup.string(),
  })

  const defaultValues = {
    amount: billing?.amount || null, // NOTE: ここがundefinedだと未入力時に0が表示されてしまうことがあるため
    detail: billing?.detail,
    attachment_images: billing?.attachment_images || [],
    settlement_method: settlement,
  } as FieldValues

  const formMethods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    trigger,
    formState: { errors, isValid, isSubmitting },
    watch,
  } = formMethods

  const watchSettlement = watch('settlement_method')
  const watchAmount = watch('amount')

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSendingModal, setShowSendingModal] = useState(false)
  const [showVirusErrorModal, setShowVirusErrorModal] = useState<
    string | undefined
  >()
  const [defaultAttachmentImages, setDefaultAttachmentImages] = useState<
    ImageProps[]
  >([])
  const [billed, setBilled] = useState(false)
  const onClickSaveMemo = useCallback(() => setShowConfirmModal(true), [])

  const changeable = settlement !== 'no_charge'

  useEffect(() => {
    if (watchSettlement === 'no_charge') {
      setValue('amount', 0)
    } else if (watchAmount === 0) {
      // NOTE: no_chargeにした後に戻すパターン
      setValue('amount', '')
    }
    if (watchSettlement !== 'link') {
      trigger('detail')
    }

    // NOTE: 一度モーダルを閉じるとwatchSettlementがundefinedになってしまうのでその場合はsettlementの値で判定
    if (
      !isConnectedStripe &&
      (watchSettlement
        ? watchSettlement === 'credit_card'
        : settlement === 'credit_card')
    ) {
      setApiError(
        t(
          'errors.Stripe決済設定が解除されているため、利用できません。同じアカウントで再接続を行ってから請求操作を行ってください。'
        )
      )
    } else {
      setApiError(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnectedStripe, watchSettlement, open])

  useEffect(() => {
    reset(defaultValues)
    setBilled(false)
    setDefaultAttachmentImages(defaultValues?.attachment_images || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billings, settlement])

  const submitDisabled =
    !isValid ||
    isSubmitting ||
    (settlement === 'bank_transfer' && !bankTransfer) || // NOTE: 銀行振込の場合は決済口座の設定が必要
    (watchSettlement === 'credit_card' && !isConnectedStripe) // NOTE: 予約後にStripe接続が解除された場合

  const settlementsSelectItems: () => SettlementNameProps[] = () => {
    switch (settlement) {
      case 'credit_card':
        return [settlement, 'link', 'bank_transfer', 'next_visit', 'no_charge']
      case 'link':
        return [settlement, 'bank_transfer', 'next_visit', 'no_charge']
      case 'cod':
        return [settlement, 'bank_transfer', 'next_visit', 'no_charge']
      default:
        return [settlement, 'no_charge']
    }
  }

  const cancelHandler = () => {
    reset(defaultValues)
    onCancel()
  }

  const submitHandler = (data: FieldValues) => {
    if (data.attachment_images?.length) {
      setShowSendingModal(true)
    }
    onSubmit(data as BillingForm)
      .then(() => {
        if (data.settlement_method === 'credit_card') {
          setBilled(true)
        }
      })
      .catch((e) => {
        if (e instanceof OfflineError) {
          setApiError(
            t(
              'errors.ネットワークに繋がっていません。通信環境を確認してください。'
            )
          )
          setBilled(false)
          return
        }
        if (e instanceof ApiErrorWithMessage) {
          if (
            // NOTE: 添付ファイルのウイルス感染
            e.has({
              type: 'infected_error',
            }) &&
            data.attachment_images
          ) {
            const message = e.body
            if (!Array.isArray(message?.values?.infected_indexes)) {
              return
            }
            const fileNames = message?.values?.infected_indexes
              .map(
                (index) =>
                  data.attachment_images?.[index as unknown as number]
                    ?.file_name
              )
              .join(' ')
            fileNames &&
              setShowVirusErrorModal(
                t(
                  'errors.添付したファイル（{{fileNames}}）に問題が発生したため、送信をキャンセルしました。ウィルスチェックソフトなどで、ファイルのスキャンを実行してください。',
                  { fileNames }
                )
              )
            // NOTE: EditableThumbnailListの初期値をリセット,再送信の場合は画像のみ前の状態に戻す
            if (defaultValues.attachment_images?.length) {
              // NOTE: 画像の数で変更を検知しているのでいったん0にする
              setDefaultAttachmentImages([])
              setTimeout(() => {
                setValue('attachment_images', defaultValues.attachment_images)
                setDefaultAttachmentImages(defaultValues.attachment_images)
              }, 100)
            } else {
              setValue('attachment_images', [])
              setDefaultAttachmentImages([])
            }
            setTimeout(() => {
              setError('attachment_images', {
                type: 'infected_error',
                message: t(
                  'errors.添付ファイルに問題があったため削除されました。'
                ),
              })
            }, 2000)
            return
          }
          if (
            // NOTE: 既に請求済みの場合
            e.has({
              key: 'settlement_status',
              value: 'billed',
            })
          ) {
            setApiError(t('errors.すでに請求済です。'))
            setBilled(true)
            return
          }
          if (
            // NOTE: StripeカスタマーIDの取得に失敗した場合
            e.has({
              key: 'stripe_customer_id',
              value: 'exists',
            })
          ) {
            setApiError(
              t(
                'errors.Stripe決済設定が解除されているため、利用できません。同じアカウントで再接続を行ってから請求操作を行ってください。'
              )
            )
            setBilled(false)
            return
          }
          if (
            // NOTE: メール送信エラー
            e.has({
              key: 'email',
              value: 'send',
            })
          ) {
            if (settlement === 'credit_card') {
              // NOTE: Stripe決済処理は完了しているのでここで再度送信した場合はサーバーではメール送信のみになる
              setApiError(
                t(
                  'errors.決済は完了しましたがメール送信ができませんでした。再度「送信」ボタンを押してください。'
                )
              )
            } else {
              setApiError(
                t('errors.送信に失敗しました。もう一度お試しください。')
              )
            }
            setBilled(false)
            return
          }
          if (
            // NOTE: 特定のStripeAPIエラー
            e.has({
              type: 'server_error',
              key: 'stripe',
              value: 'Your card was declined.',
            })
          ) {
            setApiError(
              t(
                'errors.決済が実行できませんでした。カードが拒否されています。申込者に「銀行振込」または「次回来院時」をご案内ください。'
              )
            )
            setBilled(false)
            return
          }
          // NOTE: その他の予期しないサーバーエラー、StripeAPIエラー
          setApiError(
            t(
              'errors.エラーが発生し、決済が実行できませんでした。申込者に「銀行振込」または「次回来院時」をご案内ください。'
            )
          )
          setBilled(false)
          return
        }
        // NOTE: その他の予期しないエラー
        setApiError(
          t(
            'errors.エラーが発生し、決済が実行できませんでした。申込者に「銀行振込」または「次回来院時」をご案内ください。'
          )
        )
        setBilled(false)
      })
      .finally(() => setShowSendingModal(false))
  }

  const Description: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => <div className={classes.descriptionItem}>{children}</div>

  return (
    <Modal large open={open} handleClose={cancelHandler}>
      <Modal
        open={!!showVirusErrorModal}
        handleClose={() => setShowVirusErrorModal(undefined)}
      >
        <ConfirmContent
          title={showVirusErrorModal}
          submitLabel={t('閉じる')}
          onSubmit={() => setShowVirusErrorModal(undefined)}
        />
      </Modal>
      <Modal open={showSendingModal} handleClose={() => {}}>
        <SendingContent
          message={t(
            'アップロードしたファイルをスキャン中です。この処理には数分かかることがございます。'
          )}
        />
      </Modal>
      <Paper className={classes.root}>
        <form>
          <StackItem
            label={t('決済方法')}
            alignTop={changeable ? theme.spacing(3.5) : false}
          >
            {changeable ? (
              <FormControl variant="outlined" error={!!apiError} fullWidth>
                <Controller
                  name={'settlement_method'}
                  control={control}
                  render={({ field }) => (
                    <Select {...field} displayEmpty>
                      {settlementsSelectItems().map((name) => (
                        <MenuItem key={name} value={name}>
                          {t(SettlementsNames[name])}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {apiError && <FormHelperText>{apiError}</FormHelperText>}
              </FormControl>
            ) : (
              <>
                <Text1>{t(SettlementsNames[settlement])}</Text1>
                {apiError && <FormHelperText error>{apiError}</FormHelperText>}
              </>
            )}
            {watchSettlement === 'bank_transfer' && bankTransfer && (
              <Description>
                <BankInfo bankTransfer={bankTransfer} />
              </Description>
            )}
            {watchSettlement === 'credit_card' && (
              <Description>
                <Caption>
                  <StripeLink />
                </Caption>
              </Description>
            )}
            {changeable && (
              <Description>
                <Caption>
                  {t('決済方法を変更する場合は、申込者に直接ご連絡ください。')}
                </Caption>
              </Description>
            )}
          </StackItem>
          {watchSettlement === 'no_charge' ? (
            <StackItem label={t('請求金額')}>
              <TextField
                variant="outlined"
                fullWidth
                InputProps={{
                  className: classes.disabled,
                  endAdornment: (
                    <InputAdornment position="end">{t('円')}</InputAdornment>
                  ),
                }}
                disabled
              />
            </StackItem>
          ) : (
            <FormProvider {...formMethods}>
              <Amount name="amount" />
            </FormProvider>
          )}
          <StackItemTextField
            placeholder={
              watchSettlement === 'link'
                ? t(
                    'オンライン決済サービスで発行した支払いリンクをこちらに貼り付けてください。\nその他、患者さんへの連絡事項があれば記載してください'
                  )
                : ''
            }
            defaultValue={billing?.detail}
            label={t('明細')}
            name="detail"
            control={control}
            errorMessage={errors?.detail?.message}
            multiline
            fullWidth
            rows={6}
            alignTop
            labelStatus={watchSettlement === 'link' ? undefined : 'optional'}
          />
          <StackItem
            label={
              <>
                {t('添付(任意)')}
                <UploadQuestionTooltip />
              </>
            }
            alignTop
          >
            <Controller
              name="attachment_images"
              control={control}
              render={({ field }) => (
                <EditableThumbnailList
                  {...field}
                  images={defaultAttachmentImages}
                  onChangeImages={(data) => {
                    if (!isEqual(defaultAttachmentImages, data)) {
                      setValue('attachment_images', data, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }}
                  injectedErrorMessage={errors?.attachment_images?.message}
                  maxLength={3}
                  maxFileSize={2 * 1024 * 1024}
                  reloadable
                />
              )}
            />
          </StackItem>
          <Stack align="right" borderTop>
            <MarginRightCancelButton onClick={cancelHandler} />
            <Button
              color="primary"
              variant="contained"
              disabled={submitDisabled || billed}
              onClick={onClickSaveMemo}
            >
              {submitLabel}
            </Button>
          </Stack>
          <ConfirmModal
            open={showConfirmModal}
            amount={watchAmount}
            onCancel={() => setShowConfirmModal(false)}
            onSubmit={() => {
              if (!showConfirmModal) return // NOTE: 送信成功後 消えるトランジションの間にクリックできないように
              handleSubmit(submitHandler)().finally(() =>
                setShowConfirmModal(false)
              )
            }}
            submitDisabled={submitDisabled}
            submitLabel={submitLabel}
          />
        </form>
      </Paper>
    </Modal>
  )
}

export default ReservationBillingModal
